import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Snackbar,
  Tab,
  Tabs,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  SmartToy,
  AttachMoney,
  Speed,
  Assessment,
  Timeline,
  BarChart,
  PieChart,
  ShowChart,
  Insights,
  Analytics as AnalyticsIcon,
  Refresh,
  Download,
  DateRange,
  FilterList,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ComposedChart,
  Legend,
  ScatterChart,
  Scatter,
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../contexts/ApiContext';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ErrorAlert from '../../components/Common/ErrorAlert';

interface AnalyticsData {
  overview: {
    totalUsers: number;
    activeUsers: number;
    totalBots: number;
    activeBots: number;
    totalTrades: number;
    totalVolume: number;
    totalProfit: number;
    avgSuccessRate: number;
  };
  userMetrics: {
    registrations: Array<{ date: string; count: number }>;
    activity: Array<{ date: string; active: number; total: number }>;
    retention: Array<{ cohort: string; day1: number; day7: number; day30: number }>;
    geography: Array<{ country: string; users: number; percentage: number }>;
  };
  tradingMetrics: {
    volume: Array<{ date: string; volume: number; trades: number }>;
    performance: Array<{ date: string; profit: number; successRate: number }>;
    exchanges: Array<{ exchange: string; volume: number; trades: number; successRate: number }>;
    symbols: Array<{ symbol: string; volume: number; trades: number; profit: number }>;
  };
  botMetrics: {
    creation: Array<{ date: string; created: number; activated: number }>;
    performance: Array<{ strategy: string; bots: number; avgProfit: number; successRate: number }>;
    status: Array<{ status: string; count: number; percentage: number }>;
    distribution: Array<{ exchange: string; bots: number; avgPerformance: number }>;
  };
  systemMetrics: {
    performance: Array<{ date: string; responseTime: number; uptime: number; errorRate: number }>;
    usage: Array<{ date: string; apiCalls: number; bandwidth: number; activeConnections: number }>;
    errors: Array<{ date: string; errors: number; type: string }>;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Analytics: React.FC = () => {
  const api = useApi();
  
  // State management
  const [tabValue, setTabValue] = useState(0);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
    end: new Date(),
  });
  const [timeframe, setTimeframe] = useState('30d');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch analytics data
  const { data: analyticsData, isLoading, error, refetch } = useQuery({
    queryKey: ['analytics', timeframe, dateRange],
    queryFn: () => api.get(`/admin/analytics?timeframe=${timeframe}&start=${dateRange.start.toISOString()}&end=${dateRange.end.toISOString()}`),
    refetchInterval: 300000, // Refresh every 5 minutes
  });

  const analytics = analyticsData as AnalyticsData;

  // Event handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleTimeframeChange = (newTimeframe: string) => {
    setTimeframe(newTimeframe);
    
    // Update date range based on timeframe
    const now = new Date();
    let start = new Date();
    
    switch (newTimeframe) {
      case '7d':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case '1y':
        start = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
    }
    
    setDateRange({ start, end: now });
  };

  // Utility functions
  const formatCurrency = (amount: number) => {
    if (amount >= 1e9) {
      return `$${(amount / 1e9).toFixed(2)}B`;
    } else if (amount >= 1e6) {
      return `$${(amount / 1e6).toFixed(2)}M`;
    } else if (amount >= 1e3) {
      return `$${(amount / 1e3).toFixed(2)}K`;
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(amount);
    }
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString();
  };

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert error={error} />;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Advanced Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive insights and performance metrics across the platform
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Timeframe</InputLabel>
              <Select
                value={timeframe}
                label="Timeframe"
                onChange={(e) => handleTimeframeChange(e.target.value)}
              >
                <MenuItem value="7d">Last 7 days</MenuItem>
                <MenuItem value="30d">Last 30 days</MenuItem>
                <MenuItem value="90d">Last 90 days</MenuItem>
                <MenuItem value="1y">Last year</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => refetch()}
              size="small"
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              size="small"
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Overview Cards */}
        {analytics?.overview && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      <People />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {formatNumber(analytics.overview.totalUsers)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Users
                      </Typography>
                      <Typography variant="caption" color="success.main">
                        {formatNumber(analytics.overview.activeUsers)} active
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                      <SmartToy />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {formatNumber(analytics.overview.totalBots)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Bots
                      </Typography>
                      <Typography variant="caption" color="success.main">
                        {formatNumber(analytics.overview.activeBots)} active
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                      <AttachMoney />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {formatCurrency(analytics.overview.totalVolume)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Volume
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatNumber(analytics.overview.totalTrades)} trades
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ 
                      bgcolor: analytics.overview.totalProfit >= 0 ? 'success.main' : 'error.main', 
                      mr: 2 
                    }}>
                      {analytics.overview.totalProfit >= 0 ? <TrendingUp /> : <TrendingDown />}
                    </Avatar>
                    <Box>
                      <Typography 
                        variant="h6"
                        color={analytics.overview.totalProfit >= 0 ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(analytics.overview.totalProfit)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total P&L
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatPercentage(analytics.overview.avgSuccessRate)} success rate
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Analytics Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="analytics tabs">
              <Tab label="User Analytics" icon={<People />} />
              <Tab label="Trading Analytics" icon={<TrendingUp />} />
              <Tab label="Bot Analytics" icon={<SmartToy />} />
              <Tab label="System Analytics" icon={<Assessment />} />
            </Tabs>
          </Box>

          {/* User Analytics Tab */}
          <CustomTabPanel value={tabValue} index={0}>
            {analytics?.userMetrics && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        User Registrations & Activity
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={analytics.userMetrics.registrations}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis yAxisId="left" />
                          <YAxis yAxisId="right" orientation="right" />
                          <RechartsTooltip />
                          <Legend />
                          <Bar yAxisId="left" dataKey="count" fill="#8884d8" name="New Registrations" />
                          <Line yAxisId="right" type="monotone" dataKey="active" stroke="#82ca9d" name="Active Users" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        User Geography
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <RechartsPieChart>
                          <Pie
                            data={analytics.userMetrics.geography}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ country, percentage }) => `${country} ${percentage.toFixed(1)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="users"
                          >
                            {analytics.userMetrics.geography.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </CustomTabPanel>

          {/* Trading Analytics Tab */}
          <CustomTabPanel value={tabValue} index={1}>
            {analytics?.tradingMetrics && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Trading Volume & Count
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={analytics.tradingMetrics.volume}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis yAxisId="left" />
                          <YAxis yAxisId="right" orientation="right" />
                          <RechartsTooltip />
                          <Legend />
                          <Area yAxisId="left" type="monotone" dataKey="volume" fill="#8884d8" stroke="#8884d8" name="Volume ($)" />
                          <Line yAxisId="right" type="monotone" dataKey="trades" stroke="#82ca9d" name="Trade Count" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Trading Performance
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={analytics.tradingMetrics.performance}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis yAxisId="left" />
                          <YAxis yAxisId="right" orientation="right" />
                          <RechartsTooltip />
                          <Legend />
                          <Bar yAxisId="left" dataKey="profit" fill="#82ca9d" name="Profit ($)" />
                          <Line yAxisId="right" type="monotone" dataKey="successRate" stroke="#ffc658" name="Success Rate (%)" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </CustomTabPanel>

          {/* Bot Analytics Tab */}
          <CustomTabPanel value={tabValue} index={2}>
            {analytics?.botMetrics && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Bot Creation & Activation
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={analytics.botMetrics.creation}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <RechartsTooltip />
                          <Legend />
                          <Area type="monotone" dataKey="created" stackId="1" stroke="#8884d8" fill="#8884d8" name="Created" />
                          <Area type="monotone" dataKey="activated" stackId="1" stroke="#82ca9d" fill="#82ca9d" name="Activated" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Bot Status Distribution
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <RechartsPieChart>
                          <Pie
                            data={analytics.botMetrics.status}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ status, percentage }) => `${status} ${percentage.toFixed(1)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="count"
                          >
                            {analytics.botMetrics.status.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </CustomTabPanel>

          {/* System Analytics Tab */}
          <CustomTabPanel value={tabValue} index={3}>
            {analytics?.systemMetrics && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        System Performance
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={analytics.systemMetrics.performance}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis yAxisId="left" />
                          <YAxis yAxisId="right" orientation="right" />
                          <RechartsTooltip />
                          <Legend />
                          <Line yAxisId="left" type="monotone" dataKey="responseTime" stroke="#8884d8" name="Response Time (ms)" />
                          <Line yAxisId="right" type="monotone" dataKey="uptime" stroke="#82ca9d" name="Uptime (%)" />
                          <Line yAxisId="left" type="monotone" dataKey="errorRate" stroke="#ff7300" name="Error Rate" />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        System Usage
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={analytics.systemMetrics.usage}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <RechartsTooltip />
                          <Legend />
                          <Area type="monotone" dataKey="apiCalls" stackId="1" stroke="#8884d8" fill="#8884d8" name="API Calls" />
                          <Area type="monotone" dataKey="activeConnections" stackId="2" stroke="#82ca9d" fill="#82ca9d" name="Active Connections" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </CustomTabPanel>
        </Paper>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default Analytics;
